// Ad-hoc consumption log ("I just drank X"). Cloud-aware mirror of the APK
// store. localStorage cache + Supabase round-trip when signed in.

import { supabase, isCloudMode, currentUserSync } from './supabase';

export type ConsumptionEntry = {
  id: string;
  compound_id: string;
  qty: number | null;
  qty_unit: string | null;
  source: string | null;
  notes: string | null;
  consumed_at: string; // ISO
  created_at: string;
};

const K = 'mystack:consumption:v1';

function safeParse<T>(s: string | null, fallback: T): T {
  if (!s) return fallback;
  try { return JSON.parse(s) as T; } catch { return fallback; }
}

import { uuidV4 } from './analytics';

function loadLocal(): ConsumptionEntry[] {
  return safeParse<ConsumptionEntry[]>(localStorage.getItem(K), []);
}
function saveLocal(all: ConsumptionEntry[]): void {
  localStorage.setItem(K, JSON.stringify(all));
}

async function fetchCloud(): Promise<ConsumptionEntry[] | null> {
  const u = currentUserSync();
  if (!u) return null;
  const { data, error } = await supabase
    .from('mystack_consumption_log')
    .select('id, compound_id, qty, qty_unit, source, notes, consumed_at, created_at')
    .eq('owner_id', u.id)
    .order('consumed_at', { ascending: false });
  if (error || !data) return null;
  return data.map((r) => ({
    id: (r as any).id,
    compound_id: (r as any).compound_id,
    qty: (r as any).qty,
    qty_unit: (r as any).qty_unit,
    source: (r as any).source,
    notes: (r as any).notes,
    consumed_at: (r as any).consumed_at,
    created_at: (r as any).created_at,
  }));
}

async function persistCloud(e: ConsumptionEntry): Promise<void> {
  const u = currentUserSync();
  if (!u) return;
  await supabase.from('mystack_consumption_log').upsert({
    id: e.id, owner_id: u.id, compound_id: e.compound_id,
    qty: e.qty, qty_unit: e.qty_unit, source: e.source, notes: e.notes,
    consumed_at: e.consumed_at,
  });
}

export async function loadAll(): Promise<ConsumptionEntry[]> {
  if (await isCloudMode()) {
    const c = await fetchCloud();
    if (c) { saveLocal(c); return c; }
  }
  const list = loadLocal();
  list.sort((a, b) => new Date(b.consumed_at).getTime() - new Date(a.consumed_at).getTime());
  return list;
}

export async function loadToday(): Promise<ConsumptionEntry[]> {
  const all = await loadAll();
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return all.filter((e) => new Date(e.consumed_at).getTime() >= start);
}

export async function recentUnique(limit = 10): Promise<ConsumptionEntry[]> {
  const all = await loadAll();
  const seen = new Set<string>();
  const out: ConsumptionEntry[] = [];
  for (const e of all) {
    const key = `${e.compound_id}|${e.source ?? ''}|${e.qty ?? ''}|${e.qty_unit ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(e);
      if (out.length >= limit) break;
    }
  }
  return out;
}

export async function addConsumption(opts: {
  compound_id: string;
  qty?: number | null;
  qty_unit?: string | null;
  source?: string | null;
  notes?: string | null;
  consumed_at?: Date;
}): Promise<ConsumptionEntry> {
  const e: ConsumptionEntry = {
    id: uuidV4(),
    compound_id: opts.compound_id,
    qty: opts.qty ?? null,
    qty_unit: opts.qty_unit ?? null,
    source: opts.source ?? null,
    notes: opts.notes ?? null,
    consumed_at: (opts.consumed_at ?? new Date()).toISOString(),
    created_at: new Date().toISOString(),
  };
  const all = loadLocal();
  all.push(e);
  saveLocal(all);
  if (await isCloudMode()) await persistCloud(e);
  return e;
}

export async function removeConsumption(id: string): Promise<void> {
  saveLocal(loadLocal().filter((e) => e.id !== id));
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      await supabase.from('mystack_consumption_log').delete().eq('owner_id', u.id).eq('id', id);
    }
  }
}
